import { produce } from 'immer';
import { combineReducers } from 'redux';
import * as types from './types';
import { findBoolExpression, findParent, getNewExpression, insertAfter, pushItem, insertBefore, unshiftItem, removeItem, group, explode } from './utils';
import _ from 'lodash';

// const getSideId: (side: types.ExpSide) => 'lhs' | 'rhs' = (side) => side == types.ExpSide.left ? 'lhs' : 'rhs';

export const findExpressionPath: (
    fullExp: types.Expression,
    pathLocation: types.PathLocation
) => types.ExpressionPath = (fullExp, pathLocation) => {
    const { filterPath, side, argPath, depth, rhsIndex } = pathLocation;
    let expression = findBoolExpressionPath(fullExp, filterPath, side, rhsIndex);

    for (const ap of argPath) {
        for (let d = 0; d < ap.depth; d++) {
            expression = expression.path as types.ExpressionPath;
        }
        let func: types.ExpressionFunction = expression.func!;
        let arg: types.ExpressionFunctionArg = func.args[ap.argIndex];
        expression = arg;
    }

    if (depth != null) {
        for (let d = 0; d < depth; d++) {
            expression = expression.path as types.ExpressionPath;
        }
    }

    return expression;
}

export const findBoolExpressionPath: (
    expression: types.Expression,
    path: types.FilterPath,
    side: types.ExpSide,
    rhsIndex?: number,
) => types.ExpressionPath = (expression, path, side, rhsIndex) => {
    const boolExp = findBoolExpression(expression, path);
    return side == types.ExpSide.left ? boolExp.lhs : boolExp.rhs[rhsIndex!];
}



const initialListState: types.FilterState = {
    expression: getNewExpression(),
    disabled: false,
}

const filterReducer = (
    state = initialListState,
    action: types.FilterActions
) => {
    return produce(state, draft => {
        if (action.type === types.FILTER_NEGATE) {
            const { path, value } = action;
            const expression = findBoolExpression(draft.expression, path);
            expression.negated = value;

        } else if (action.type === types.FILTER_SELECT) {
            const { path, value } = action;
            const expression = findBoolExpression(draft.expression, path);
            expression.selected = value;

        } else if (action.type === types.FILTER_ADD_AFTER) {
            const { path } = action;
            const newExp = getNewExpression();

            if (path.length > 0) {
                const parent = findParent(draft.expression, path);
                insertAfter(parent, _.last(path)!, newExp);
            } else {
                pushItem(draft.expression, newExp);
            }

        } else if (action.type === types.FILTER_ADD_BEFORE) {
            const { path } = action;
            const newExp = getNewExpression();

            if (path.length > 0) {
                const parent = findParent(draft.expression, path);
                insertBefore(parent, _.last(path)!, newExp);
            } else {
                unshiftItem(draft.expression, newExp);
            }

        } else if (action.type === types.FILTER_REMOVE) {
            const { path } = action;
            const parent = findParent(draft.expression, path);
            removeItem(parent, _.last(path)!);

        } else if (action.type === types.FILTER_SET_LOG_OP) {
            const { path, index, op } = action;
            const expression = findBoolExpression(draft.expression, path);
            expression.ops[index] = op;

        } else if (action.type === types.FILTER_GROUP) {
            const { path } = action;
            const expression = findBoolExpression(draft.expression, path);
            group(expression);

        } else if (action.type === types.FILTER_EXPLODE) {
            const { path } = action;
            explode(draft.expression, path);

        } else if (action.type === types.FILTER_SET_OP) {
            const { path, op } = action;
            const expression = findBoolExpression(draft.expression, path);
            expression.op = op;

        } else if (action.type === types.FILTER_SET_DISABLED) {
            const { value } = action;
            draft.disabled = value;

        } else if (action.type === types.FILTER_SET_PATH) {
            const { attr, pathLocation, field } = action;
            const expression = findExpressionPath(draft.expression, pathLocation);
            expression.path = attr;
            expression.field = field;
            delete expression.func;

        } else if (action.type === types.FILTER_SET_FUNC) {
            const { pathLocation: { depth }, spec, pathLocation } = action;
            const expression = findExpressionPath(draft.expression, pathLocation);

            const args: types.ExpressionFunctionArg[] = [];

            for (const argSpec of spec.args) {
                args.push({ path: '' });
            }

            if (depth == 0) {
                const newExpPath: types.ExpressionPath = {
                    path: expression.path,
                    field: expression.field,
                    func: {
                        spec,
                        args,
                    }
                }

                expression.path = newExpPath;
                delete expression.func;
                delete expression.field;

            } else {
                expression.func = {
                    spec,
                    args,
                }
                const { pathLocation: { argPath, filterPath, side } } = action;
                const root = findExpressionPath(draft.expression, { argPath, filterPath, side });
                root.path = expression;
            }

        } else if (action.type === types.FILTER_REMOVE_FUNC) {
            const { pathLocation: { argPath, filterPath, side, rhsIndex }, pathLocation } = action;
            const expression = findExpressionPath(draft.expression, pathLocation);
            const root = findExpressionPath(draft.expression, { argPath, filterPath, side, rhsIndex });
            delete expression.func;
            root.path = expression.path;
            root.field = expression.field;
            root.func = expression.func;

        } else if (action.type === types.FILTER_SET_LITERAL) {
            const { value, pathLocation } = action;
            const expression = findExpressionPath(draft.expression, pathLocation);
            expression.path = { value };
            delete expression.field;
            delete expression.func;
        } else if (action.type === types.FILTER_RESET) {
            draft.disabled = initialListState.disabled;
            draft.expression = getNewExpression();
        } else if (action.type === types.FILTER_SET_EXPRESSION) {
            draft.disabled = initialListState.disabled;
            draft.expression = action.expression;
        }
    });
};

const reducer = combineReducers({
    filter: filterReducer,
});

export default reducer;
